import React, { useState } from 'react';
import PropTypes from 'prop-types';
import 'styles/components/TripResult';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatDuration } from 'utils/Helpers';
import {
  Text,
  Button,
  FlatButton,
  ResultProvider,
  Spacing,
  OutlineButton,
  Icon,
} from '@reservamos/elements';
import { getCurrencyDecimals, getCurrencySuffix } from 'utils/currency';
import TripSchedule from 'ui/atoms/TripSchedule';
import TimeIcon from 'ui/atoms/TimeIcon';
import useWhitelabelTheme from 'hooks/whitelabel/useWhitelabelTheme';
import useQuickPurchaseCTAExperiment from 'hooks/useQuickPurchaseCTAExperiment';
import { isIAMSA } from 'utils/Reserbus';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import { isOverbookingEligible } from 'utils/overbooking';
import { tripHasHotSaleDiscount, tripHasAnniversaryIcon } from 'utils/specialDiscount';
import PreviousPrice from 'ui/atoms/PreviousPrice';
import getInternetDiscountPercent from 'constants/discountPercentage';
import getRouteLabel from 'utils/trips/getRouteLabel';
import { formatSectionDate } from 'utils/afterMidnightVariant';
import getClosedTripButtonText from 'utils/trips/getClosedTripButtonText';
import { hasUrgencyDiscountAmount, isResultDiscountUIType } from 'utils/urgencyDiscount';
import petFriendly from '../../images/icons/footPrint.svg';
import quickPurchaseIconSrc from '../../images/purchases/quick-purchase.png';
import ProviderInfo from './ProviderInfo/ProviderInfo';
import '../../ui/layouts/MatrixResult';
import TripFiltersBadges from './TripFiltersBadges';
import NewPriceComponent from './NewPriceComponent';
import TripDiscountAdvice from './TripDiscountAdvice';
import ResultCategoriesAvailability from './ResultCategoriesAvailability';
import WalletPrice from './WalletPrice';

/**
 * This result is used by:
 * - IAMSA (ETN, Costaline, Autovias)
 * - Expreso Brasilia
 * - Smilebus
 */

// hide the duration if is minor than 16min
// because this is probably wrong estimation
const MIN_DURATION = 15;
/**
 * Render the card of a trip on the search results
 * @param {Object} props
 * @param {String} props.id - Trip Id.
 * @param {string} props.arrival - Arrival time of the trip
 * @param {string} props.departure - Departure time of the trip
 * @param {number} props.duration - Duration of the trip in minutes
 * @param {function} props.onSelectClick - Function to handle the selection of a trip
 * @param {Object} props.providerDiscount - Provider discount information
 * @param {number} props.total - Total price of the trip
 * @param {Object} props.trip - Trip information
 * @param {boolean} props.isExchange - Flag indicating if it is an exchange trip
 * @param {function} props.handleShowTripDetails - Function to handle showing trip details
 * @param {boolean} props.highLightType - Flag indicating if the trip is highlighted
 * @param {string} props.highlightText - Text to highlight
 * @param {string} props.sparkleBadgeLabel - Text for the highlighted sparkle badge
 * @param {Array} props.providerDiscounts - Array of provider discounts
 * @param {string} props.useFlatFare - Show use flat fare
 * @param {number} props.walletPoints - Total price in wallet points
 * @param {Object} props.filtersApplied - Applied filters
 * @param {string} props.serviceType - Type of service
 * @param {string} props.way - Trip way
 * @param {number} props.showWalletPoints - Number of wallet points to show
 * @param {Object} props.pricing - Pricing information
 * @param {string} props.walletType - Wallet type
 * @param {boolean} props.highLightAsMobile - Flag indicating if the trip is highlighted as mobile
 * @param {Array} props.availableCategories - The available categories for the trip.
 * @param {boolean} props.showNewFiltersList - Flag indicating if the new filters list should be shown
 * @param {boolean} props.discountBase - Flag indicating if the discount is base
 * @param {number} props.totalBeforeDiscount - Total price before discount
 * @param {number} [props.officePrice] - The office (window) price from the provider.
 * @param {boolean} props.isOpen - Flag indicating if the trip is open
 * @param {node} props.children - Children nodes
 * @param {node} props.recentView - Recent view node
 * @param {boolean} props.isSeatsOnResults - Flag indicating if the seats should be shown on the results
 * @param {string} props.destination - The destination of the trip.
 * @param {string} props.origin - The origin of the trip.
 * @param {boolean} props.shouldShowProviderDiscount - Flag indicating if the provider discount advice should be shown
 * @param {boolean} props.isNewUser - Flag indicating if the user is new
 * @param {boolean} props.isClosed - Flag indicating if the trip is closed (sold out or departed)
 * @param {boolean} props.showQuickPurchase - Flag indicating if the quick purchase button should be shown
 * @param {Function} props.onQuickPurchaseClick - Handler for quick purchase button click
 * @param {boolean} props.isRecommendedList - Flag indicating if the result is inside the recommended trips list
 * @param {boolean} props.isCategoryPrice - Whether the displayed total is a searched-category price
 * @returns {React.Component} - ResultSpecifics component
 */
/** Hoisted: the class list does not fit the repo's 100-column width inside the attribute. */
const SECTION_DATE_CAPSULE = [
  'flex-none whitespace-nowrap rounded-full',
  'bg-[#1C99B7] px-3 py-0.5 text-[13px] font-bold text-white',
].join(' ');

const TripResult = ({
  arrival,
  departure,
  duration,
  onSelectClick,
  providerDiscount,
  total,
  trip,
  isExchange,
  totalBase,
  handleShowTripDetails,
  highLightType,
  highlightText,
  sparkleBadgeLabel,
  providerDiscounts,
  id,
  useFlatFare,
  walletPoints,
  filtersApplied,
  way,
  showWalletPoints,
  pricing,
  walletType,
  highLightAsMobile,
  serviceType,
  showNewFiltersList,
  availableCategories,
  discountBase,
  totalBeforeDiscount,
  officePrice,
  isOpen,
  children,
  recentView,
  isSeatsOnResults,
  destination,
  origin,
  shouldShowProviderDiscount,
  isNewUser,
  isClosed,
  showQuickPurchase,
  onQuickPurchaseClick,
  isRecommendedList,
  isCategoryPrice,
}) => {
  const { theme, brand, tripPriceDecimals, searchWidget } = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');
  const { getServiceType, serviceTypes, resultsCTAColor } = useWhitelabelTheme();
  const {
    passengerTypes,
    stops,
    variableDepartureTime,
    line,
    secondFloor,
    isPetFriendly,
    service,
    redirectTo,
    path,
  } = trip;

  const [showDetailsModal, setShowDetailsModal] = useState(true);
  const { t } = useTranslation();
  const quickPurchaseCTACopy = useQuickPurchaseCTAExperiment();

  const showDuration = duration > MIN_DURATION;
  const showFlatFare = useFlatFare === 'valid';
  const isOversold = isOverbookingEligible(trip);

  /**
   * Determines the call-to-action button text based on trip state
   * @returns {string} The appropriate button text
   */
  const getCallToActionText = () => {
    if (isClosed) {
      return getClosedTripButtonText(t, { departure, availability: trip.availability });
    }
    if (isOversold) return t('general:select');
    if (isSeatsOnResults && !redirectTo) return t('general:view_seats');

    return t('general:select');
  };

  const buttonText = isOpen ? t('general:close') : getCallToActionText();

  const colorPrice = providerDiscount ? 'discount' : 'grayMedium';
  const departureHour = moment(departure).format('LT');
  const arrivalHour = moment(arrival).format('LT');
  const DOTERS_CURRENCY = t('search:doters_currency');

  /**
   * Prices
   */
  const primaryCurrency = showFlatFare ? DOTERS_CURRENCY : getCurrencySuffix();
  let primaryPrice = total;
  if (showFlatFare) primaryPrice = walletPoints;
  if (totalBase && !showFlatFare) primaryPrice = totalBase;
  const currencyDoters = primaryCurrency === DOTERS_CURRENCY;

  const earlyDiscount = pricing?.discountType === 'early_trip_discount';
  const hasHotSaleDiscount = tripHasHotSaleDiscount({
    features,
    brand,
    providerDiscount,
    departureDate: departure,
    originCode: trip.originCode,
    destinationCode: trip.destinationCode,
  });

  const providerAnniversary = tripHasAnniversaryIcon({
    features,
    brand,
    providerDiscount,
    departureDate: departure,
    originCode: trip.originCode,
    destinationCode: trip.destinationCode,
  });

  const stopsText = trip.hasConnections
    ? t('search:trip_stopovers_connection', { count: stops })
    : t('search:trip_stopovers', { count: stops, context: stops === 0 ? 'direct' : null });

  const routeCity = features.SHOW_ROUTE_LABEL
    ? getRouteLabel(path, features.ROUTE_LABEL_CITIES)
    : null;
  const routeLabelText = routeCity ? t('search:route_via', { city: routeCity }) : null;

  /**
   * Selects the appropriate service label based on the feature flags and theme settings.
   * @returns {string} The service label.
   */
  const selectServiceLabel = () => {
    if (features.SHOW_TRIP_SERVICE_LABEL) {
      return service;
    }

    const { useLineNameAsServiceType = false } = theme.serviceTypes || {};
    const serviceProp = serviceTypes ? service : serviceType;
    return useLineNameAsServiceType ? line?.name ?? '' : getServiceType(serviceProp).label;
  };

  const serviceTypeLabel = selectServiceLabel();
  const hasProviderDiscount = hasUrgencyDiscountAmount(providerDiscount);
  const hasResultDiscountUIType = isResultDiscountUIType(pricing?.discountType);
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const showDiscountUrgencyUI =
    shouldShowProviderDiscount &&
    hasProviderDiscount &&
    hasResultDiscountUIType &&
    urgencyDiscountUIEnabled;

  /**
   * Cross out the adult/general price when a gratuity category price is shown below it.
   * Sources the adult price from totalBeforeDiscount so it works without the backend
   * office_price, gated on the half-price fallback flag so other brands are unaffected.
   */
  const showGratuityCrossedOutPrice =
    Boolean(halfPriceCategoryFallback) &&
    Boolean(isCategoryPrice) &&
    Number(totalBeforeDiscount) > Number(total);
  /**
   * Cross out the provider's office (window) price above the price actually charged. Guarded on
   * `isCategoryPrice`, not `showGratuityCrossedOutPrice` (which also needs the half-price GrowthBook
   * flag): officePrice is the general-category table fare, so it must never be crossed out over a
   * category total. Connections are excluded for the same reason: their total spans every leg, while
   * officePrice is a single-leg table fare. Upstream drops the field when merging legs, so that
   * clause is defence in depth rather than the thing doing the work today.
   * SHOW_OFFICE_PRICE defaults to true, so this is live wherever the provider emits officePrice —
   * not opt-in per tenant.
   *
   * Both amounts are rounded to the tenant's decimals first: this guard decides what is displayed,
   * so it has to run at the precision that is displayed. On a tenant with `tripPriceDecimals: 0` a
   * total of 15.25 and a table fare of 15.34 both render "15", and crossing one out over the other
   * claims a discount that is not on screen.
   */
  const displayedTotal = Number(Number(total).toFixed(tripPriceDecimals));
  const displayedOfficePrice = Number(Number(officePrice).toFixed(tripPriceDecimals));
  const hasOfficePrice =
    Boolean(features.SHOW_OFFICE_PRICE) &&
    !trip.hasConnections &&
    !isCategoryPrice &&
    displayedOfficePrice > displayedTotal;
  const showPriceWithoutDiscount =
    (hasProviderDiscount && (features.SHOW_OFFICE_PRICE || showDiscountUrgencyUI)) ||
    showGratuityCrossedOutPrice;
  const showTerminals = features.SHOW_TERMINALS_ON_RESULTS;

  /**
   * Method to handle the selection of a trip
   * @param {String} funnelSection - section of the funnel where the trip was selected
   */
  const handleOnSelect = (funnelSection) => {
    const funnelSectionSelected = funnelSection === 'details' ? 'details' : 'results';
    onSelectClick(funnelSectionSelected);
    if (isExchange) setShowDetailsModal(false);
  };

  const quickPurchaseButton = (
    <div className="flex gap-2 pl-2">
      <OutlineButton
        isRounded
        variant={resultsCTAColor}
        text={buttonText}
        onClick={handleOnSelect}
        padding="L"
        mobileSize="S"
      />
      {isRecommendedList ? (
        <Button
          onClick={onQuickPurchaseClick}
          padding="L"
          minHeight="41px"
          variant="quickPurchase"
          isRounded
          mobileSize="S"
        >
          <img
            src={quickPurchaseIconSrc}
            alt="Quick Purchase"
            height="auto"
            width={12}
            className="mr-1"
          />
          <span className="leading-normal">{quickPurchaseCTACopy}</span>
        </Button>
      ) : (
        <div className="group">
          <OutlineButton
            onClick={onQuickPurchaseClick}
            padding="L"
            variant="quickPurchase"
            isRounded
            mobileSize="S"
          >
            <img
              src={quickPurchaseIconSrc}
              alt="Quick Purchase"
              height="auto"
              width={12}
              className="mr-1"
            />
            <span className="bg-[linear-gradient(115deg,#B3ADFA,#BB75FF)] bg-clip-text text-transparent leading-normal group-hover:text-white">
              {quickPurchaseCTACopy}
            </span>
          </OutlineButton>
        </div>
      )}
    </div>
  );

  const contentTypes = [
    <Text size="XS" weight="regular" color="grayLight" textAlign="center">
      {stopsText}
    </Text>,
  ];
  if (features.SHOW_VARIABLE_DEPARTURE_TIME) {
    contentTypes.push(
      <Text size="XS" weight="regular" color="grayLight" textAlign="center">
        {variableDepartureTime
          ? `${t('search:trip_service_on_the_way')}`
          : `${t('search:trip_service_local')}`}
      </Text>,
    );
  }

  const detailsComponent =
    showDetailsModal && !isClosed ? (
      <ProviderInfo
        amenities={line?.services}
        providerDiscounts={providerDiscounts}
        initialTab={0}
        availabilityCategories={passengerTypes}
        tripId={id}
        earlyDiscount={earlyDiscount}
        buttonText={buttonText}
        onSelectClick={handleOnSelect}
        departure={departure}
        arrival={arrival}
        duration={duration}
        price={!isExchange && primaryPrice}
        colorPrice={colorPrice}
        currency={primaryCurrency}
        decimals={currencyDoters ? 0 : getCurrencyDecimals()}
        sign={currencyDoters ? '' : '$'}
        currencyLowercase={showFlatFare}
        showFlatFare={showFlatFare}
        isOnTheWay={variableDepartureTime}
      >
        <div className="matrix-mobile-click">
          <FlatButton size="S" mobileSize="XS" onClick={handleShowTripDetails}>
            {t('general:details')}
          </FlatButton>
        </div>
      </ProviderInfo>
    ) : (
      <></>
    );

  const filtersComponent = showNewFiltersList ? (
    <TripFiltersBadges filtersApplied={filtersApplied} trip={trip} minimalDesign />
  ) : (
    <ResultCategoriesAvailability availableCategories={availableCategories} />
  );

  const hidePriceOnExchange = features.HIDE_PRICE_ON_EXCHANGE && isExchange;
  const isIAMSABrand = isIAMSA(brand);
  const relocateDesktopServiceType = brand === 'expressosaoluiz';
  const hasDiscount =
    isIAMSABrand && Boolean(discountBase !== undefined ? discountBase : providerDiscount);
  const floorsLabel = secondFloor ? t('search:double_floor_new') : t('search:one_floor_new');
  const showDotersSecondaryPrice = isIAMSABrand && (walletPoints > 0 || showFlatFare);
  const previousPrice =
    (showPriceWithoutDiscount && (
      <PreviousPrice price={totalBeforeDiscount} lineThrough={showGratuityCrossedOutPrice} />
    )) ||
    (hasOfficePrice && <PreviousPrice price={officePrice} />) ||
    null;
  const internetDiscountPercent = getInternetDiscountPercent(isNewUser, way);

  // AM-1617: show the available-seats count on results (marketplace parity with the
  // legacy card). Opt-in per brand via SHOW_AVAILABLE_SEATS_ON_RESULTS so ETN/Brasilia
  // are unaffected. Informational (not the low-availability urgency nudge). Hidden when
  // sold out (availability 0) so it doesn't contradict the "Agotado" CTA.
  const availableSeats = trip.availability;
  const showAvailableSeats = features.SHOW_AVAILABLE_SEATS_ON_RESULTS && availableSeats > 0;

  /**
   * Render the provider discount advice badge.
   * @returns {JSX.Element} The provider discount advice.
   */
  const renderProviderDiscount = () => (
    <TripDiscountAdvice
      discountType={pricing?.discountType}
      discountAvailability={pricing?.discountAvailability}
      discountAmount={providerDiscount?.amount}
      discountPercent={providerDiscount?.percent}
      tripTotal={total}
      way={way}
    />
  );

  /**
   * Render the internet discount label, shown to new IAMSA users without a provider/early discount.
   * @returns {JSX.Element} The internet discount label.
   */
  const renderInternetDiscount = () => (
    <span className="bg-primary-regular/10 rounded-full p-1.5 text-xs text-primary-regular flex items-center justify-center font-semibold italic">
      {t('general:internet', { discount: internetDiscountPercent })}
    </span>
  );

  /**
   * Resolve the badge for the result's discount slot.
   * Hidden when the Hot Sale icon is shown next to the price to avoid duplicating the discount.
   * @returns {JSX.Element|null} The provider discount advice, the internet label, or null.
   */
  const renderDiscountBadge = () => {
    if (hasHotSaleDiscount || hidePriceOnExchange || showDiscountUrgencyUI) return null;
    if (shouldShowProviderDiscount) return renderProviderDiscount();

    const showInternetDiscount =
      Boolean(internetDiscountPercent) && isIAMSABrand && isNewUser && !earlyDiscount;
    return showInternetDiscount ? renderInternetDiscount() : null;
  };

  return (
    <ResultProvider
      brandLayout={brand}
      brand={features.SHOW_RESULT_LINE_NAME ? line?.name ?? '' : ''}
      logoUrl={line?.logoUrl}
      iconDeparture={
        /* AM-1617: reservamos falls back to the 25px iconDeparture grid column; the
           default size-M weather icon (25px) collides with the departure time around
           the ~768px breakpoint. Force size S so it always clears the column. */
        <TimeIcon time={departure} size={brand === 'reservamos' ? 'S' : 'M'} />
      }
      serviceType={
        isIAMSABrand ? floorsLabel : features.SHOW_SERVICE_TYPE_BADGE && serviceTypeLabel
      }
      relocateDesktopServiceType={relocateDesktopServiceType}
      duration={
        showDuration && (
          <Text size="XS" mediumSize="M" color="grayMedium">
            {formatDuration(duration)}
          </Text>
        )
      }
      discount={renderDiscountBadge()}
      schedule={
        <>
          <Spacing size="XS" alignItems="center" justifyContent="space-between" fullWidth>
            <Spacing size="L" alignItems="center" justifyContent="space-between" fullWidth>
              <TripSchedule
                departureHour={departureHour}
                arrivalHour={arrivalHour}
                mainContent={contentTypes}
                mobileSize="L"
                departureTerminal={origin?.name}
                arrivalTerminal={destination?.name}
                showTerminals={showTerminals}
                hideMainContent={features.HIDE_DIRECT_LABEL_WITH_NO_STOPS && stops === 0}
              />
              {/* The small-hours trips leave on the NEXT day, and the card otherwise shows only
                  clock times: without the date, `00:10` reads as tonight. */}
              {trip?.sectionSource === 'after_midnight' && (
                <span className={SECTION_DATE_CAPSULE}>
                  {formatSectionDate(departure, searchWidget?.dateFormat)}
                </span>
              )}
            </Spacing>
            {isIAMSABrand && isPetFriendly && <Icon size="S" type={petFriendly} />}
          </Spacing>
          {routeLabelText && (
            <Text size="XS" weight="regular" color="grayLight" textAlign="center">
              {routeLabelText}
            </Text>
          )}
        </>
      }
      details={detailsComponent}
      filter={filtersComponent}
      button={
        /* eslint-disable no-nested-ternary */
        isClosed ? (
          <div className="inline-flex items-center justify-center px-6 py-2 rounded-full border-2 border-gray-200 bg-gray-100 text-gray-400 font-semibold cursor-not-allowed min-w-[120px]">
            {buttonText}
          </div>
        ) : showQuickPurchase ? (
          quickPurchaseButton
        ) : highLightType ? (
          <Button
            text={buttonText}
            onClick={handleOnSelect}
            padding="L"
            size="M"
            weight="semibold"
            variant={resultsCTAColor}
            isRounded
          />
        ) : (
          <OutlineButton
            isRounded
            variant={resultsCTAColor}
            text={buttonText}
            onClick={handleOnSelect}
          />
        )
        /* eslint-enable no-nested-ternary */
      }
      price={
        !hidePriceOnExchange ? (
          <NewPriceComponent
            total={total}
            walletTotal={walletPoints}
            showOfficePriceBadge={Boolean(features.DISCOUNT_BADGE_RESULT_ENABLED)}
            officePrice={hasOfficePrice ? officePrice : null}
            hasHotSaleDiscount={hasHotSaleDiscount}
            providerAnniversary={providerAnniversary}
            highLightType={highLightType}
            walletType={walletType}
            showWalletTotal={showWalletPoints}
            extraWalletText={
              showFlatFare &&
              t('purchase:label.reward_ticket', { context: features.FLAT_FARE_CONTEXT })
            }
            useDiscountCurrency={hasDiscount}
            lighterText={isClosed || isOpen}
            tripAvailability={trip.availability}
            lowAvailability={trip.availability <= features.LOW_AVAILABILITY_THRESHOLD}
            providerDiscount={providerDiscount}
            discountType={pricing?.discountType}
            totalBeforeDiscount={totalBeforeDiscount}
            discountAvailability={pricing?.discountAvailability}
            discountExpiresAt={pricing?.meta?.availableUntil}
            showDiscountUrgency={showDiscountUrgencyUI}
            secondaryPrice={
              <>
                {showDotersSecondaryPrice ? (
                  <Spacing
                    size="S"
                    alignItems="center"
                    justifyContent="flex-end"
                    isResponsive
                    mobileAlign="flex-end"
                    responsiveSize="XS"
                  >
                    {previousPrice}
                    <WalletPrice
                      walletType="doters"
                      walletTotal={walletPoints}
                      size="S"
                      mobileSize="S"
                      iconSize="XS"
                      showFlatFare={showFlatFare}
                    />
                  </Spacing>
                ) : (
                  previousPrice
                )}
                {showAvailableSeats && (
                  <Text size="XS" color="grayLight" italic>
                    {t('search:available_seats', { count: availableSeats })}
                  </Text>
                )}
              </>
            }
          />
        ) : null
      }
      changeLayout={features.CHANGE_RESULT_LAYOUT}
      highlight={highLightType}
      highlightText={highlightText}
      sparkleBadgeLabel={sparkleBadgeLabel}
      mobileLayout={highLightAsMobile}
      highlightMinHeight={highLightAsMobile}
      isOpen={isOpen}
      recentView={recentView}
      recentViewText={t('trips:recently_seen')}
    >
      {children}
    </ResultProvider>
  );
};

const placeType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  cityName: PropTypes.string.isRequired,
});

TripResult.propTypes = {
  arrival: PropTypes.string.isRequired,
  departure: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  onSelectClick: PropTypes.func.isRequired,
  origin: placeType.isRequired,
  destination: placeType.isRequired,
  providerDiscount: PropTypes.any,
  total: PropTypes.number.isRequired,
  totalBeforeDiscount: PropTypes.number,
  officePrice: PropTypes.number,
  isCategoryPrice: PropTypes.bool,
  trip: PropTypes.shape({
    // Stamped by `parseAfterMidnightSection`; tells a small-hours card from a normal one.
    sectionSource: PropTypes.string,
    line: PropTypes.shape({
      logoUrl: PropTypes.string.isRequired,
      services: PropTypes.arrayOf(PropTypes.string).isRequired,
      name: PropTypes.string,
    }).isRequired,
    pricing: PropTypes.shape({
      discountType: PropTypes.string,
      discountAvailability: PropTypes.number,
      totalBeforeDiscount: PropTypes.number,
      meta: PropTypes.shape({
        availableUntil: PropTypes.string,
      }),
    }),
    passengerTypes: PropTypes.array,
    variableDepartureTime: PropTypes.bool,
    secondFloor: PropTypes.bool,
    stops: PropTypes.number,
    hasConnections: PropTypes.bool,
    service: PropTypes.string,
    isPetFriendly: PropTypes.bool,
    redirectTo: PropTypes.string,
    availability: PropTypes.number,
    path: PropTypes.array,
    originCode: PropTypes.string,
    destinationCode: PropTypes.string,
  }).isRequired,
  highLightType: PropTypes.bool,
  isExchange: PropTypes.bool,
  handleShowTripDetails: PropTypes.func,
  providerDiscounts: PropTypes.array,
  useFlatFare: PropTypes.string,
  walletPoints: PropTypes.number,
  totalBase: PropTypes.number,
  filtersApplied: PropTypes.object,
  secondFloor: PropTypes.bool,
  way: PropTypes.string,
  showWalletPoints: PropTypes.number,
  pricing: PropTypes.shape({
    discountType: PropTypes.string,
    discountAvailability: PropTypes.number,
    meta: PropTypes.shape({
      availableUntil: PropTypes.string,
    }),
  }),
  walletType: PropTypes.string,
  highLightAsMobile: PropTypes.bool,
  serviceType: PropTypes.string,
  showNewFiltersList: PropTypes.bool,
  availableCategories: PropTypes.array,
  discountBase: PropTypes.bool,
  isOpen: PropTypes.bool,
  children: PropTypes.node,
  recentView: PropTypes.node,
  highlightText: PropTypes.string,
  sparkleBadgeLabel: PropTypes.string,
  isSeatsOnResults: PropTypes.bool,
  shouldShowProviderDiscount: PropTypes.bool,
  isNewUser: PropTypes.bool,
  isClosed: PropTypes.bool,
  showQuickPurchase: PropTypes.bool,
  onQuickPurchaseClick: PropTypes.func,
  isRecommendedList: PropTypes.bool,
};

TripResult.defaultProps = {
  providerDiscount: null,
  officePrice: null,
  providerDiscounts: [],
  shouldShowProviderDiscount: true,
  isNewUser: false,
  isClosed: false,
};

export default TripResult;
